/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  ToolUsageTraceLogGroup,
  ToolUsageTraceLogGroup$inboundSchema,
} from "./toolusagetraceloggroup.js";

/**
 * Hook execution status when the row came from hook telemetry
 */
export const ToolUsageTraceSummaryHookStatus = {
  Success: "success",
  Failure: "failure",
  Blocked: "blocked",
  Pending: "pending",
} as const;
/**
 * Hook execution status when the row came from hook telemetry
 */
export type ToolUsageTraceSummaryHookStatus = OpenEnum<
  typeof ToolUsageTraceSummaryHookStatus
>;

/**
 * Tool usage aggregation target kind
 */
export const ToolUsageTraceSummaryTargetKind = {
  Server: "server",
  LocalTools: "local_tools",
  Skill: "skill",
} as const;
/**
 * Tool usage aggregation target kind
 */
export type ToolUsageTraceSummaryTargetKind = OpenEnum<
  typeof ToolUsageTraceSummaryTargetKind
>;

/**
 * Tool usage target type
 */
export const ToolUsageTraceSummaryTargetType = {
  HostedMcpServer: "hosted_mcp_server",
  TunneledMcpServer: "tunneled_mcp_server",
  MetaMcpServer: "meta_mcp_server",
  ShadowMcpServer: "shadow_mcp_server",
  LocalTool: "local_tool",
  Skill: "skill",
} as const;
/**
 * Tool usage target type
 */
export type ToolUsageTraceSummaryTargetType = OpenEnum<
  typeof ToolUsageTraceSummaryTargetType
>;

/**
 * Tool usage user identity kind
 */
export const ToolUsageTraceSummaryUserKind = {
  Email: "email",
  ExternalUserId: "external_user_id",
  UserId: "user_id",
  AgentId: "agent_id",
  Unknown: "unknown",
} as const;
/**
 * Tool usage user identity kind
 */
export type ToolUsageTraceSummaryUserKind = OpenEnum<
  typeof ToolUsageTraceSummaryUserKind
>;

/**
 * A single target-aware tool usage trace row
 */
export type ToolUsageTraceSummary = {
  /**
   * AI account classification ('team' or 'personal'); empty/absent when unclassified
   */
  accountType?: string | undefined;
  /**
   * Email of the authorizer when they resolve to a known user
   */
  authorizerLabel?: string | undefined;
  /**
   * Person an agent call acted on behalf of; absent for direct calls and for agents that sign in as themselves
   */
  authorizerUserId?: string | undefined;
  /**
   * Hook block reason when hook_status is blocked
   */
  blockReason?: string | undefined;
  /**
   * Stable MCP client identity used by filters; 'unattributed' when the caller never reported one
   */
  clientKey: string;
  /**
   * User-facing MCP client label as the client reported it
   */
  clientLabel: string;
  /**
   * MCP client version when the client reported one alongside its name
   */
  clientVersion?: string | undefined;
  /**
   * Telemetry event source
   */
  eventSource: string;
  /**
   * Speakeasy URN associated with the trace
   */
  gramUrn: string;
  /**
   * Hook plugin source when the row came from hook telemetry
   */
  hookSource?: string | undefined;
  /**
   * Hook execution status when the row came from hook telemetry
   */
  hookStatus?: ToolUsageTraceSummaryHookStatus | undefined;
  /**
   * HTTP status code when available
   */
  httpStatusCode?: number | undefined;
  /**
   * Stable row identity for React keys and expansion state
   */
  id: string;
  /**
   * Number of logs in the trace
   */
  logCount: number;
  /**
   * Descriptor used by the dashboard to fetch child logs for a trace row
   */
  logGroup: ToolUsageTraceLogGroup;
  /**
   * MCP server id for hosted and tunneled targets; absent for other targets
   */
  mcpServerId?: string | undefined;
  /**
   * Earliest log timestamp in Unix nanoseconds as a string for JavaScript integer safety
   */
  startTimeUnixNano: string;
  /**
   * Stable target identifier used by filters
   */
  targetId: string;
  /**
   * Tool usage aggregation target kind
   */
  targetKind: ToolUsageTraceSummaryTargetKind;
  /**
   * User-facing target label
   */
  targetLabel: string;
  /**
   * Tool usage target type
   */
  targetType: ToolUsageTraceSummaryTargetType;
  /**
   * Tool name shown in the row
   */
  toolName: string;
  /**
   * Real OTel trace ID when the grouped logs have one
   */
  traceId?: string | undefined;
  /**
   * Stable user identity value
   */
  userKey: string;
  /**
   * Tool usage user identity kind
   */
  userKind: ToolUsageTraceSummaryUserKind;
  /**
   * User-facing user identity label
   */
  userLabel: string;
  /**
   * Gateway (meta MCP server) that dispatched this call to the target; absent for direct calls and for calls observed against a gateway itself
   */
  viaMetaMcpServerId?: string | undefined;
  /**
   * Display name of the dispatching gateway; a deleted gateway keeps its last name
   */
  viaMetaMcpServerName?: string | undefined;
};

/** @internal */
export const ToolUsageTraceSummaryHookStatus$inboundSchema: z.ZodMiniType<
  ToolUsageTraceSummaryHookStatus,
  unknown
> = openEnums.inboundSchema(ToolUsageTraceSummaryHookStatus);

/** @internal */
export const ToolUsageTraceSummaryTargetKind$inboundSchema: z.ZodMiniType<
  ToolUsageTraceSummaryTargetKind,
  unknown
> = openEnums.inboundSchema(ToolUsageTraceSummaryTargetKind);

/** @internal */
export const ToolUsageTraceSummaryTargetType$inboundSchema: z.ZodMiniType<
  ToolUsageTraceSummaryTargetType,
  unknown
> = openEnums.inboundSchema(ToolUsageTraceSummaryTargetType);

/** @internal */
export const ToolUsageTraceSummaryUserKind$inboundSchema: z.ZodMiniType<
  ToolUsageTraceSummaryUserKind,
  unknown
> = openEnums.inboundSchema(ToolUsageTraceSummaryUserKind);

/** @internal */
export const ToolUsageTraceSummary$inboundSchema: z.ZodMiniType<
  ToolUsageTraceSummary,
  unknown
> = z.pipe(
  z.object({
    account_type: z.optional(z.string()),
    authorizer_label: z.optional(z.string()),
    authorizer_user_id: z.optional(z.string()),
    block_reason: z.optional(z.string()),
    client_key: z.string(),
    client_label: z.string(),
    client_version: z.optional(z.string()),
    event_source: z.string(),
    gram_urn: z.string(),
    hook_source: z.optional(z.string()),
    hook_status: z.optional(ToolUsageTraceSummaryHookStatus$inboundSchema),
    http_status_code: z.optional(z.int()),
    id: z.string(),
    log_count: z.int(),
    log_group: ToolUsageTraceLogGroup$inboundSchema,
    mcp_server_id: z.optional(z.string()),
    start_time_unix_nano: z.string(),
    target_id: z.string(),
    target_kind: ToolUsageTraceSummaryTargetKind$inboundSchema,
    target_label: z.string(),
    target_type: ToolUsageTraceSummaryTargetType$inboundSchema,
    tool_name: z.string(),
    trace_id: z.optional(z.string()),
    user_key: z.string(),
    user_kind: ToolUsageTraceSummaryUserKind$inboundSchema,
    user_label: z.string(),
    via_meta_mcp_server_id: z.optional(z.string()),
    via_meta_mcp_server_name: z.optional(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      "account_type": "accountType",
      "authorizer_label": "authorizerLabel",
      "authorizer_user_id": "authorizerUserId",
      "block_reason": "blockReason",
      "client_key": "clientKey",
      "client_label": "clientLabel",
      "client_version": "clientVersion",
      "event_source": "eventSource",
      "gram_urn": "gramUrn",
      "hook_source": "hookSource",
      "hook_status": "hookStatus",
      "http_status_code": "httpStatusCode",
      "log_count": "logCount",
      "log_group": "logGroup",
      "mcp_server_id": "mcpServerId",
      "start_time_unix_nano": "startTimeUnixNano",
      "target_id": "targetId",
      "target_kind": "targetKind",
      "target_label": "targetLabel",
      "target_type": "targetType",
      "tool_name": "toolName",
      "trace_id": "traceId",
      "user_key": "userKey",
      "user_kind": "userKind",
      "user_label": "userLabel",
      "via_meta_mcp_server_id": "viaMetaMcpServerId",
      "via_meta_mcp_server_name": "viaMetaMcpServerName",
    });
  }),
);

export function toolUsageTraceSummaryFromJSON(
  jsonString: string,
): SafeParseResult<ToolUsageTraceSummary, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ToolUsageTraceSummary$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ToolUsageTraceSummary' from JSON`,
  );
}
